import { useEffect, useState } from "react";
import { TrendingUp, TrendingDown, Percent, AlertTriangle, Wallet, ArrowUpRight, Award, Users } from "lucide-react";
import api, { formatPrice } from "../../lib/api";

const MONTH_LABELS = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

function monthLabel(ym) {
    if (!ym || ym.length < 7) return ym || "—";
    const [y, m] = ym.split("-");
    return `${MONTH_LABELS[parseInt(m, 10) - 1] || m} ${y.slice(2)}`;
}

/**
 * Verkaufs-Profitabilität (Phase 8 Financials) — Übersichtsblock im Händler-Dashboard.
 * Zeigt Margensumme des laufenden Monats, Ø-Marge %, Ampel-Verteilung und rote Deals.
 */
export default function SalesProfitabilityCard() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(false);

    useEffect(() => {
        api.get("/dealer/dispo/profitability")
            .then((r) => setData(r.data))
            .catch(() => setError(true));
    }, []);

    if (error) return null;
    if (!data) {
        return (
            <div className="rounded-sm border-2 border-gray-200 p-6 animate-pulse text-sm text-gray-400" data-testid="profitability-loading">
                Profitabilität wird geladen …
            </div>
        );
    }

    const { this_month, totals, monthly, red_deals } = data;
    const hasDeals = (totals?.count || 0) > 0;
    const topBrands = data.top_brands || [];
    const flopBrands = data.flop_brands || [];
    const bySeller = data.by_seller || [];
    const showSellers = bySeller.length > 1 || (bySeller[0] && bySeller[0].seller_id);
    const cmpCur = data.compare_months?.current;
    const cmpPrev = data.compare_months?.previous;

    return (
        <div className="mb-8" data-testid="sales-profitability-card">
            <h3 className="font-display font-black text-xl mb-1 flex items-center gap-2">
                <Wallet className="w-5 h-5 text-emerald-600" /> Verkaufs-Profitabilität
            </h3>
            <p className="text-sm text-gray-500 mb-4">
                Marge aus abgeschlossenen Dispositionen (EK, Bonis, interne Kosten · Phase-8-Finanzen).
            </p>

            {!hasDeals ? (
                <div className="rounded-sm border-2 border-dashed border-gray-200 p-6 text-sm text-gray-500" data-testid="profitability-empty">
                    Noch keine Finanzen erfasst. Tragen Sie unter „Finanzen &amp; Checkliste" eines verkauften
                    Inserats EK, Bonis und interne Kosten ein, um Ihre Marge auszuwerten.
                </div>
            ) : (
                <>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="profitability-kpis">
                        {/* Margensumme aktueller Monat */}
                        <div className="rounded-sm border-2 border-emerald-200 bg-emerald-50/60 p-4" data-testid="kpi-month-margin">
                            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 mb-1">
                                <TrendingUp className="w-3.5 h-3.5" /> Marge {monthLabel(this_month.month)}
                            </div>
                            <div className="font-display font-black text-2xl font-mono-num text-emerald-700">
                                {formatPrice(this_month.margin_sum)}
                            </div>
                            <div className="text-[11px] text-gray-500 mt-0.5">{this_month.count} Verkäufe</div>
                        </div>

                        {/* Ø-Marge % */}
                        <div className="rounded-sm border-2 border-gray-200 p-4" data-testid="kpi-avg-margin">
                            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">
                                <Percent className="w-3.5 h-3.5" /> Ø-Marge
                            </div>
                            <div className="font-display font-black text-2xl font-mono-num">
                                {Number(totals.avg_margin_pct).toLocaleString("de-DE", { maximumFractionDigits: 1 })} %
                            </div>
                            <div className="text-[11px] text-gray-500 mt-0.5">über {totals.count} Verkäufe</div>
                        </div>

                        {/* Gesamtmarge */}
                        <div className="rounded-sm border-2 border-gray-200 p-4" data-testid="kpi-total-margin">
                            <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1">
                                <Wallet className="w-3.5 h-3.5" /> Gesamtmarge
                            </div>
                            <div className="font-display font-black text-2xl font-mono-num">
                                {formatPrice(totals.margin_sum)}
                            </div>
                            <div className="flex items-center gap-2 text-[11px] mt-1">
                                <span className="inline-flex items-center gap-1 text-emerald-600"><span className="w-2 h-2 rounded-full bg-emerald-500" />{totals.green}</span>
                                <span className="inline-flex items-center gap-1 text-amber-600"><span className="w-2 h-2 rounded-full bg-amber-500" />{totals.yellow}</span>
                                <span className="inline-flex items-center gap-1 text-red-600"><span className="w-2 h-2 rounded-full bg-red-500" />{totals.red}</span>
                            </div>
                        </div>

                        {/* Rote Deals */}
                        <div className={`rounded-sm border-2 p-4 ${totals.red > 0 ? "border-red-300 bg-red-50/60" : "border-gray-200"}`} data-testid="kpi-red-deals">
                            <div className={`flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider mb-1 ${totals.red > 0 ? "text-red-700" : "text-gray-500"}`}>
                                <AlertTriangle className="w-3.5 h-3.5" /> Kritische Marge
                            </div>
                            <div className={`font-display font-black text-2xl font-mono-num ${totals.red > 0 ? "text-red-700" : ""}`}>
                                {totals.red}
                            </div>
                            <div className="text-[11px] text-gray-500 mt-0.5">unter 5 % Marge</div>
                        </div>
                    </div>

                    {/* Monatsverlauf (kompakter Balken) */}
                    {monthly.length > 1 && (
                        <div className="mt-4 rounded-sm border-2 border-gray-200 p-4" data-testid="profitability-monthly">
                            <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-3">Margenverlauf (letzte {monthly.length} Monate)</div>
                            <div className="flex items-end gap-2 h-24">
                                {monthly.map((m) => {
                                    const max = Math.max(...monthly.map((x) => Math.abs(x.margin_sum)), 1);
                                    const h = Math.max(6, Math.round((Math.abs(m.margin_sum) / max) * 80));
                                    const neg = m.margin_sum < 0;
                                    return (
                                        <div key={m.month} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${formatPrice(m.margin_sum)} · ${m.count} Verkäufe`}>
                                            <div className={`w-full rounded-sm ${neg ? "bg-red-400" : "bg-emerald-400"}`} style={{ height: `${h}px` }} />
                                            <div className="text-[9px] text-gray-500 font-mono-num">{monthLabel(m.month)}</div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Top/Flop-Marken nach Ø-Marge — hilft beim gezielten Einkauf */}
                    {topBrands.length > 0 && (
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="profitability-brand-ranking">
                            <div className="rounded-sm border-2 border-emerald-200 overflow-hidden" data-testid="top-brands">
                                <div className="bg-emerald-50 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1.5">
                                    <Award className="w-3.5 h-3.5" /> Top-Marken (Ø-Marge)
                                </div>
                                <ul className="divide-y divide-emerald-100">
                                    {topBrands.map((b, i) => (
                                        <li key={b.brand} className="flex items-center justify-between gap-3 px-4 py-2" data-testid={`top-brand-${b.brand}`}>
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className="w-5 h-5 shrink-0 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-bold flex items-center justify-center font-mono-num">{i + 1}</span>
                                                <span className="font-bold text-sm truncate">{b.brand}</span>
                                                <span className="text-[11px] text-gray-400">· {b.count}×</span>
                                            </div>
                                            <div className="flex items-center gap-1.5 shrink-0">
                                                <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />
                                                <span className="font-mono-num font-bold text-emerald-700 text-sm">{b.avg_margin_pct} %</span>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            {flopBrands.length > 0 && (
                                <div className="rounded-sm border-2 border-red-200 overflow-hidden" data-testid="flop-brands">
                                    <div className="bg-red-50 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-red-700 flex items-center gap-1.5">
                                        <TrendingDown className="w-3.5 h-3.5" /> Schwächste Marken (Ø-Marge)
                                    </div>
                                    <ul className="divide-y divide-red-100">
                                        {flopBrands.map((b) => (
                                            <li key={b.brand} className="flex items-center justify-between gap-3 px-4 py-2" data-testid={`flop-brand-${b.brand}`}>
                                                <div className="flex items-center gap-2 min-w-0">
                                                    <span className="font-bold text-sm truncate">{b.brand}</span>
                                                    <span className="text-[11px] text-gray-400">· {b.count}×</span>
                                                </div>
                                                <div className="flex items-center gap-1.5 shrink-0">
                                                    <TrendingDown className="w-3.5 h-3.5 text-red-500" />
                                                    <span className={`font-mono-num font-bold text-sm ${b.avg_margin_pct < 0 ? "text-red-700" : "text-amber-600"}`}>{b.avg_margin_pct} %</span>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Verkäufer-Aufschlüsselung — Marge je zugewiesenem Verkäufer + Monatsvergleich */}
                    {showSellers && (
                        <div className="mt-4 rounded-sm border-2 border-gray-200 overflow-hidden" data-testid="profitability-by-seller">
                            <div className="bg-gray-50 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-gray-600 flex items-center justify-between gap-2">
                                <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Marge je Verkäufer</span>
                                <span className="text-[10px] font-semibold text-gray-400 normal-case tracking-normal">{monthLabel(cmpCur)} vs. {monthLabel(cmpPrev)}</span>
                            </div>
                            <ul className="divide-y divide-gray-100">
                                {bySeller.map((sv) => (
                                    <li key={sv.seller_id || "none"} className="flex items-center justify-between gap-3 px-4 py-2.5" data-testid={`seller-row-${sv.seller_id || "none"}`}>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2 min-w-0">
                                                <span className="font-bold text-sm truncate">{sv.name}</span>
                                                <span className="text-[11px] text-gray-400">· {sv.count} Verkäufe</span>
                                            </div>
                                            {/* Monatsvergleich aktueller vs. Vormonat */}
                                            <div className="flex items-center gap-2 mt-0.5 text-[11px]" data-testid={`seller-trend-${sv.seller_id || "none"}`}>
                                                {sv.trend === "up" && <TrendingUp className="w-3.5 h-3.5 text-emerald-600" />}
                                                {sv.trend === "down" && <TrendingDown className="w-3.5 h-3.5 text-red-500" />}
                                                {sv.trend === "flat" && <span className="w-3.5 text-center text-gray-400">–</span>}
                                                <span className="text-gray-500 font-mono-num">{formatPrice(sv.this_month_margin)}</span>
                                                <span className="text-gray-300">/</span>
                                                <span className="text-gray-400 font-mono-num">{formatPrice(sv.prev_month_margin)}</span>
                                                <span className={`font-mono-num font-bold ${sv.delta > 0 ? "text-emerald-600" : sv.delta < 0 ? "text-red-500" : "text-gray-400"}`}>
                                                    {sv.delta > 0 ? "+" : ""}{formatPrice(sv.delta)}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3 shrink-0">
                                            <span className="font-mono-num font-bold text-sm">{formatPrice(sv.margin_sum)}</span>
                                            <span className="font-mono-num text-[11px] text-gray-500 w-16 text-right">Ø {sv.avg_margin_pct} %</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Liste roter Deals mit Direktlink zur Disposition */}
                    {red_deals.length > 0 && (
                        <div className="mt-4 rounded-sm border-2 border-red-200 overflow-hidden" data-testid="profitability-red-list">
                            <div className="bg-red-50 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-red-700 flex items-center gap-1.5">
                                <AlertTriangle className="w-3.5 h-3.5" /> Deals mit kritischer Marge
                            </div>
                            <ul className="divide-y divide-red-100">
                                {red_deals.map((d, i) => (
                                    <li key={`${d.listing_id}-${i}`} className="flex items-center justify-between gap-3 px-4 py-2.5" data-testid={`red-deal-${d.listing_id}`}>
                                        <div className="min-w-0">
                                            <div className="font-bold text-sm truncate">{d.title || "Inserat"}</div>
                                            <div className="text-[11px] text-gray-500">{monthLabel(d.month)} · Verkauf {formatPrice(d.sale_gross)}</div>
                                        </div>
                                        <div className="flex items-center gap-3 shrink-0">
                                            <div className="text-right">
                                                <div className="font-mono-num font-bold text-red-600 text-sm">{formatPrice(d.margin_net)}</div>
                                                <div className="text-[11px] text-red-500">{d.margin_pct} %</div>
                                            </div>
                                            <a
                                                href={`/edit-listing/${d.listing_id}?focus=disposition`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#111827] hover:text-red-600"
                                                data-testid={`red-deal-link-${d.listing_id}`}
                                            >
                                                Öffnen <ArrowUpRight className="w-3.5 h-3.5" />
                                            </a>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
